import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { useCallback, useMemo } from "react";

import {
  EditableDataGrid,
  type EditableDataGridColDef,
} from "~/components/editable_data_grid";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import type { clientRedirectUriSchema } from "~/entities/create_client";

export type RedirectUriWithId = {
  id: string;
  uri: string;
};

interface RedirectURIsEditorWithIdsProps {
  value: RedirectUriWithId[];
  onChange: (value: RedirectUriWithId[]) => void;
  loading?: boolean;
  canDelete?: boolean;
  canEdit?: boolean;
  canAdd?: boolean;
  zodSchema?: typeof clientRedirectUriSchema;
  error?: string;
  redirectUriTooltipMessage?: string;
  // Optional callback for inline save per row. If not provided, uses form-based workflow.
  onSave?: (row: RedirectUriWithId) => void;
  // Optional callback for inline delete per row. If not provided, uses form-based workflow.
  onDelete?: (row: RedirectUriWithId) => void;
}

export function RedirectURIsEditorWithIds({
  value,
  onChange,
  loading,
  canDelete,
  canEdit,
  canAdd,
  zodSchema,
  error,
  redirectUriTooltipMessage,
  onSave,
  onDelete,
}: RedirectURIsEditorWithIdsProps) {
  const rows = value;

  const columns = useMemo<EditableDataGridColDef<RedirectUriWithId>[]>(
    () => [
      {
        field: "uri",
        headerName: "Redirect URI",
        minWidth: 200,
        flex: 1,
        editable: true,
        disableColumnMenu: true,
        resizable: false,
        sortable: false,
        ...(redirectUriTooltipMessage && {
          renderHeader: () => (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <span>Redirect URI</span>
              <Tooltip title={redirectUriTooltipMessage}>
                <span style={{ display: "inline-flex" }}>
                  <PublicSvgIcon name="info" />
                </span>
              </Tooltip>
            </Stack>
          ),
        }),
      },
    ],
    [redirectUriTooltipMessage],
  );

  const handleRowsChange = useCallback(
    (nextRows: RedirectUriWithId[]) => {
      onChange(nextRows);
    },
    [onChange],
  );

  const createEmptyRow = useCallback((): RedirectUriWithId => {
    const newRow = {
      id: crypto.randomUUID(),
      uri: "",
    };
    return newRow;
  }, []);

  return (
    <EditableDataGrid<RedirectUriWithId>
      dataGridTestId="redirect-uri-grid"
      addButtonDataTestId="add-redirect-uri-button"
      columns={columns}
      rows={rows}
      headingIconName="domain_verification"
      heading="Redirect URIs"
      addButtonLabel="Add Redirect URI"
      onRowsChange={handleRowsChange}
      createEmptyRow={createEmptyRow}
      loading={loading}
      canDelete={canDelete}
      canEdit={canEdit}
      canAdd={canAdd}
      zodSchema={zodSchema}
      error={error}
      noRowsLabel="No Redirect URIs"
      deleteDialogHeading="Delete Redirect URI"
      getDeleteDialogMessage={(row) =>
        `Are you sure you want to delete Redirect URI ${row.uri}?`
      }
      deleteDialogConfirmText="Yes, delete Redirect URI"
      deleteDialogCancelText="No, cancel"
      onSave={onSave}
      onDelete={onDelete}
    />
  );
}
